import { useEffect, useMemo, useState } from "react";
import axios from "../utils/axios";
import Navbar from "../components/Navbar";
import Spinner from "../components/Spinner";
import CollapsibleSection from "../components/CollapsibleSection";
import "./AdminPanel.css";

interface User {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
  isAdmin?: boolean;
}

interface AdminPanelProps {
  user: User;
}

interface ModuleMetadata {
  id: string;
  name: string;
  description: string;
  version: string;
  defaultConfig: Record<string, unknown>;
}

interface GuildSummary {
  id: string;
  name: string;
  icon: string | null;
  memberCount: number;
}

interface AdminLiveNotification {
  id: number;
  guild_id: string;
  guild_name: string;
  channel_id: string;
  channel_name?: string | null;
  twitch_username: string;
  message: string;
  auto_delete_previous: number;
  show_tags: number;
  auto_edit_end: number;
  is_live: number;
}

interface AdminYouTubeLiveNotification {
  id: number;
  guild_id: string;
  guild_name: string;
  channel_id: string;
  channel_name?: string | null;
  youtube_channel_id: string;
  message: string;
  auto_delete_previous: number;
  show_tags: number;
  auto_edit_end: number;
  is_live: number;
}

interface AdminDropNotification {
  id: number;
  guild_id: string;
  guild_name: string;
  channel_id: string;
  channel_name?: string | null;
  game_name: string;
  last_drop_start_at?: string | null;
  last_drop_end_at?: string | null;
}

interface LiveModuleSettings {
  value: number | null;
  defaultValue: number;
  minValue: number;
}

const hasOwn = (obj: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key);

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  const pad = (num: number) => `${num}`.padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

function AdminPanel({ user }: AdminPanelProps) {
  const [modules, setModules] = useState<ModuleMetadata[]>([]);
  const [globalVisibility, setGlobalVisibility] = useState<
    Record<string, boolean>
  >({});
  const [loadingModules, setLoadingModules] = useState(true);
  const [moduleError, setModuleError] = useState<string | null>(null);
  const [savingModuleId, setSavingModuleId] = useState<string | null>(null);

  const [guilds, setGuilds] = useState<GuildSummary[]>([]);
  const [selectedGuildId, setSelectedGuildId] = useState<string>("");
  const [guildVisibility, setGuildVisibility] = useState<
    Record<string, boolean>
  >({});
  const [loadingGuilds, setLoadingGuilds] = useState(true);
  const [loadingGuildVisibility, setLoadingGuildVisibility] = useState(false);
  const [guildError, setGuildError] = useState<string | null>(null);
  const [savingGuildModuleId, setSavingGuildModuleId] = useState<string | null>(
    null,
  );
  const [leavingGuildId, setLeavingGuildId] = useState<string | null>(null);

  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [loadingFeatures, setLoadingFeatures] = useState(true);
  const [featureError, setFeatureError] = useState<string | null>(null);
  const [savingFeatureKey, setSavingFeatureKey] = useState<string | null>(null);

  const [adminLiveNotifications, setAdminLiveNotifications] = useState<
    AdminLiveNotification[]
  >([]);
  const [loadingLiveNotifications, setLoadingLiveNotifications] =
    useState(true);
  const [liveNotificationError, setLiveNotificationError] = useState<
    string | null
  >(null);
  const [deletingLiveNotificationId, setDeletingLiveNotificationId] =
    useState<number | null>(null);
  const [adminYouTubeLiveNotifications, setAdminYouTubeLiveNotifications] =
    useState<AdminYouTubeLiveNotification[]>([]);
  const [loadingYouTubeLiveNotifications, setLoadingYouTubeLiveNotifications] =
    useState(true);
  const [youTubeLiveNotificationError, setYouTubeLiveNotificationError] =
    useState<string | null>(null);
  const [deletingYouTubeNotificationId, setDeletingYouTubeNotificationId] =
    useState<number | null>(null);
  const [liveModuleSettings, setLiveModuleSettings] = useState<
    LiveModuleSettings | null
  >(null);
  const [loadingLiveModuleSettings, setLoadingLiveModuleSettings] =
    useState(true);
  const [liveModuleSettingsError, setLiveModuleSettingsError] = useState<
    string | null
  >(null);
  const [pollIntervalInput, setPollIntervalInput] = useState<string>("");
  const [savingPollInterval, setSavingPollInterval] = useState(false);
  const [adminDropNotifications, setAdminDropNotifications] = useState<
    AdminDropNotification[]
  >([]);
  const [loadingDropNotifications, setLoadingDropNotifications] =
    useState(true);
  const [dropNotificationError, setDropNotificationError] = useState<
    string | null
  >(null);
  const [deletingDropNotificationId, setDeletingDropNotificationId] =
    useState<number | null>(null);

  useEffect(() => {
    fetchGlobalModuleVisibility();
    fetchGuilds();
    fetchFeatures();
    fetchAdminLiveNotifications();
    fetchAdminYouTubeNotifications();
    fetchLiveModuleSettings();
    fetchAdminDropNotifications();
  }, []);

  useEffect(() => {
    if (selectedGuildId) {
      fetchGuildVisibility(selectedGuildId);
    } else {
      setGuildVisibility({});
    }
  }, [selectedGuildId]);

  const fetchGlobalModuleVisibility = async () => {
    setLoadingModules(true);
    setModuleError(null);
    try {
      const response = await axios.get("/api/admin/modules/visibility");
      setModules(response.data.modules || []);
      setGlobalVisibility(response.data.visibility || {});
    } catch (error) {
      console.error("Failed to load module visibility", error);
      setModuleError(
        "Failed to load module visibility. Please try again later.",
      );
      setModules([]);
    } finally {
      setLoadingModules(false);
    }
  };

  const fetchGuilds = async (): Promise<GuildSummary[]> => {
    setLoadingGuilds(true);
    setGuildError(null);
    let fetchedGuilds: GuildSummary[] = [];
    try {
      const response = await axios.get("/api/admin/guilds");
      fetchedGuilds = response.data.guilds || [];
      setGuilds(fetchedGuilds);
      if (fetchedGuilds.length > 0 && !selectedGuildId) {
        setSelectedGuildId(fetchedGuilds[0].id);
      }
    } catch (error) {
      console.error("Failed to load guild list", error);
      setGuildError("Failed to load guild list. Please try again later.");
      setGuilds([]);
    } finally {
      setLoadingGuilds(false);
    }
    return fetchedGuilds;
  };

  const fetchGuildVisibility = async (guildId: string) => {
    setLoadingGuildVisibility(true);
    setGuildError(null);
    try {
      const response = await axios.get(
        `/api/admin/guilds/${guildId}/module-visibility`,
      );
      setGuildVisibility(response.data.visibility || {});
    } catch (error) {
      console.error("Failed to load guild module visibility", error);
      setGuildError("Failed to load module visibility for the selected guild.");
      setGuildVisibility({});
    } finally {
      setLoadingGuildVisibility(false);
    }
  };

  const fetchFeatures = async () => {
    setLoadingFeatures(true);
    setFeatureError(null);
    try {
      const response = await axios.get("/api/admin/features");
      setFeatures(response.data.features || {});
    } catch (error) {
      console.error("Failed to load feature flags", error);
      setFeatureError(
        "Failed to load feature toggles. Please try again later.",
      );
      setFeatures({});
    } finally {
      setLoadingFeatures(false);
    }
  };

  const fetchAdminLiveNotifications = async () => {
    setLoadingLiveNotifications(true);
    setLiveNotificationError(null);
    try {
      const response = await axios.get("/api/admin/live-notifications");
      setAdminLiveNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Failed to load live notifications", error);
      setLiveNotificationError(
        "Unable to load live notifications. Please try again later.",
      );
      setAdminLiveNotifications([]);
    } finally {
      setLoadingLiveNotifications(false);
    }
  };

  const fetchAdminYouTubeNotifications = async () => {
    setLoadingYouTubeLiveNotifications(true);
    setYouTubeLiveNotificationError(null);
    try {
      const response = await axios.get(
        "/api/admin/youtube-live-notifications",
      );
      setAdminYouTubeLiveNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Failed to load YouTube live notifications", error);
      setYouTubeLiveNotificationError(
        "Unable to load YouTube live notifications. Please try again later.",
      );
      setAdminYouTubeLiveNotifications([]);
    } finally {
      setLoadingYouTubeLiveNotifications(false);
    }
  };

  const fetchAdminDropNotifications = async () => {
    setLoadingDropNotifications(true);
    setDropNotificationError(null);
    try {
      const response = await axios.get("/api/admin/drop-notifications");
      setAdminDropNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Failed to load drop notifications", error);
      setDropNotificationError(
        "Failed to load drop notifications. Please try again later.",
      );
      setAdminDropNotifications([]);
    } finally {
      setLoadingDropNotifications(false);
    }
  };

  const fetchLiveModuleSettings = async () => {
    setLoadingLiveModuleSettings(true);
    setLiveModuleSettingsError(null);
    try {
      const response = await axios.get(
        "/api/admin/modules/live/settings",
      );
      const field = response.data.settings?.pollIntervalSeconds;
      const minValue =
        typeof field?.minValue === "number" ? field.minValue : 15;
      const defaultValue =
        typeof field?.defaultValue === "number"
          ? field.defaultValue
          : Math.max(minValue, 60);
      const currentValue =
        typeof field?.value === "number" && Number.isFinite(field.value)
          ? field.value
          : null;

      setLiveModuleSettings({
        value: currentValue,
        defaultValue,
        minValue,
      });
      setPollIntervalInput(currentValue ? String(currentValue) : "");
    } catch (error) {
      console.error("Failed to load live module settings", error);
      setLiveModuleSettingsError(
        "Unable to load live notification settings. Please try again later.",
      );
      setLiveModuleSettings(null);
    } finally {
      setLoadingLiveModuleSettings(false);
    }
  };

  const handleSavePollInterval = async () => {
    if (!pollIntervalInput) {
      setLiveModuleSettingsError(
        "Enter a polling interval in seconds before saving.",
      );
      return;
    }

    const parsed = Number(pollIntervalInput);
    if (!Number.isFinite(parsed)) {
      setLiveModuleSettingsError("Polling interval must be a number.");
      return;
    }

    const minValue = liveModuleSettings?.minValue ?? 15;
    if (parsed < minValue) {
      setLiveModuleSettingsError(
        `Polling interval must be at least ${minValue} seconds.`,
      );
      return;
    }

    setSavingPollInterval(true);
    setLiveModuleSettingsError(null);
    try {
      await axios.put(
        "/api/admin/modules/live/settings/pollIntervalSeconds",
        { value: parsed },
      );
      await fetchLiveModuleSettings();
    } catch (error) {
      console.error("Failed to update polling interval", error);
      setLiveModuleSettingsError(
        "Failed to update polling interval. Please try again later.",
      );
    } finally {
      setSavingPollInterval(false);
    }
  };

  const handleResetPollInterval = async () => {
    setSavingPollInterval(true);
    setLiveModuleSettingsError(null);
    try {
      await axios.put(
        "/api/admin/modules/live/settings/pollIntervalSeconds",
        { value: null },
      );
      await fetchLiveModuleSettings();
    } catch (error) {
      console.error("Failed to reset polling interval", error);
      setLiveModuleSettingsError(
        "Failed to reset polling interval. Please try again later.",
      );
    } finally {
      setSavingPollInterval(false);
    }
  };

  const handleToggleGlobalModule = async (moduleId: string) => {
    const current = hasOwn(globalVisibility, moduleId)
      ? globalVisibility[moduleId]
      : true;
    const nextValue = !current;
    setSavingModuleId(moduleId);
    try {
      await axios.put(`/api/admin/modules/${moduleId}/visibility`, {
        visible: nextValue,
      });
      setGlobalVisibility((prev) => ({
        ...prev,
        [moduleId]: nextValue,
      }));
    } catch (error) {
      console.error("Failed to update module visibility", error);
      alert("Failed to update module visibility. Please try again.");
    } finally {
      setSavingModuleId(null);
    }
  };

  const handleToggleGuildModule = async (moduleId: string) => {
    if (!selectedGuildId) return;
    const hasOverride = hasOwn(guildVisibility, moduleId);
    const current = hasOverride
      ? guildVisibility[moduleId]
      : hasOwn(globalVisibility, moduleId)
        ? globalVisibility[moduleId]
        : true;
    const nextValue = !current;
    setSavingGuildModuleId(moduleId);
    try {
      await axios.put(
        `/api/admin/guilds/${selectedGuildId}/module-visibility/${moduleId}`,
        { visible: nextValue },
      );
      setGuildVisibility((prev) => ({
        ...prev,
        [moduleId]: nextValue,
      }));
    } catch (error) {
      console.error("Failed to update guild module visibility", error);
      alert("Failed to update guild module visibility. Please try again.");
    } finally {
      setSavingGuildModuleId(null);
    }
  };

  const handleToggleFeature = async (featureKey: string) => {
    const current = features[featureKey] ?? false;
    const nextValue = !current;
    setSavingFeatureKey(featureKey);
    try {
      await axios.put(`/api/admin/features/${featureKey}`, {
        enabled: nextValue,
      });
      setFeatures((prev) => ({
        ...prev,
        [featureKey]: nextValue,
      }));
    } catch (error) {
      console.error("Failed to update feature flag", error);
      alert("Failed to update feature flag. Please try again.");
    } finally {
      setSavingFeatureKey(null);
    }
  };

  const handleAdminDeleteLive = async (
    notification: AdminLiveNotification,
  ) => {
    if (
      !confirm(
        `Remove the live notification for ${notification.twitch_username} in ${notification.guild_name}?`,
      )
    ) {
      return;
    }
    setDeletingLiveNotificationId(notification.id);
    try {
      await axios.delete(
        `/api/server/${notification.guild_id}/live-notifications/${notification.id}`,
      );
      await fetchAdminLiveNotifications();
    } catch (error) {
      console.error("Failed to delete live notification", error);
      alert("Failed to delete this live notification. Please try again.");
    } finally {
      setDeletingLiveNotificationId(null);
    }
  };

  const handleAdminDeleteYouTube = async (
    notification: AdminYouTubeLiveNotification,
  ) => {
    if (
      !confirm(
        `Remove the live notification for ${notification.youtube_channel_id} in ${notification.guild_name}?`,
      )
    ) {
      return;
    }
    setDeletingYouTubeNotificationId(notification.id);
    try {
      await axios.delete(
        `/api/server/${notification.guild_id}/youtube-live-notifications/${notification.id}`,
      );
      await fetchAdminYouTubeNotifications();
    } catch (error) {
      console.error("Failed to delete YouTube live notification", error);
      alert("Failed to delete this YouTube live notification. Please try again.");
    } finally {
      setDeletingYouTubeNotificationId(null);
    }
  };

  const handleAdminDeleteDrop = async (
    notification: AdminDropNotification,
  ) => {
    if (
      !confirm(
        `Remove the drop notification for ${notification.game_name} in ${notification.guild_name}?`,
      )
    ) {
      return;
    }

    setDeletingDropNotificationId(notification.id);
    try {
      await axios.delete(`/api/admin/drop-notifications/${notification.id}`);
      await fetchAdminDropNotifications();
    } catch (error) {
      console.error("Failed to delete drop notification", error);
      alert("Failed to delete drop notification. Please try again.");
    } finally {
      setDeletingDropNotificationId(null);
    }
  };

  const handleLeaveGuild = async () => {
    if (!selectedGuildId) {
      return;
    }
    const guild = guilds.find((g) => g.id === selectedGuildId);
    const guildName = guild?.name || "this guild";
    const confirmed = confirm(
      `Are you sure you want to remove the bot from ${guildName}? This will make the bot leave the guild immediately.`,
    );
    if (!confirmed) {
      return;
    }

    const targetGuildId = selectedGuildId;
    setLeavingGuildId(targetGuildId);
    try {
      await axios.post(`/api/server/${targetGuildId}/leave`);
      const updatedGuilds = await fetchGuilds();
      const stillExists = updatedGuilds.some((g) => g.id === targetGuildId);
      if (!stillExists) {
        const fallbackGuildId = updatedGuilds[0]?.id || "";
        setSelectedGuildId(fallbackGuildId);
        if (!fallbackGuildId) {
          setGuildVisibility({});
        }
      }
    } catch (error) {
      console.error("Failed to remove bot from guild", error);
      alert("Failed to remove the bot from the selected guild.");
    } finally {
      setLeavingGuildId(null);
    }
  };

  const selectedGuild = useMemo(
    () => guilds.find((guild) => guild.id === selectedGuildId) || null,
    [guilds, selectedGuildId],
  );

  return (
    <div className="admin-panel">
      <Navbar user={user} />
      <div className="container">
        <div className="card admin-card">
          <h1>🛠️ Admin Control Center</h1>
          <p className="admin-subtitle">
            Manage advanced bot settings that are only available to the bot
            owner.
          </p>

          <section className="admin-section">
            <div className="section-header">
              <h2>🌍 Global Module Visibility</h2>
              <p>Control which modules appear for every guild by default.</p>
            </div>
            {moduleError && <div className="error-banner">{moduleError}</div>}
            {loadingModules ? (
              <div className="loading-container">
                <Spinner />
                <p>Loading module visibility…</p>
              </div>
            ) : modules.length === 0 ? (
              <p className="empty-state">No module metadata available.</p>
            ) : (
              <div className="module-list">
                {modules.map((module) => {
                  const visible = hasOwn(globalVisibility, module.id)
                    ? globalVisibility[module.id]
                    : true;
                  const isSaving = savingModuleId === module.id;
                  const visibilityLabel = visible
                    ? "Visible everywhere"
                    : "Hidden everywhere";
                  const visibilityHint = visible
                    ? "Shown in every guild unless a guild override hides it."
                    : "Hidden for every guild unless a guild override shows it.";
                  return (
                    <div key={module.id} className="module-row">
                      <div className="module-info">
                        <div className="module-heading-row">
                          <h3>{module.name}</h3>
                          <span
                            className={`status-pill ${visible ? "status-pill--active" : "status-pill--hidden"}`}
                          >
                            {visibilityLabel}
                          </span>
                        </div>
                        <p>{module.description}</p>
                        <div className="module-meta-row">
                          <span className="module-version">
                            v{module.version}
                          </span>
                          <span className="module-visibility-hint">
                            {visibilityHint}
                          </span>
                        </div>
                      </div>
                      <button
                        className={`btn ${visible ? "btn-danger" : "btn-primary"}`}
                        onClick={() => handleToggleGlobalModule(module.id)}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <Spinner size="small" />
                        ) : visible ? (
                          "Hide Everywhere"
                        ) : (
                          "Show Everywhere"
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="admin-section">
            <div className="section-header">
              <h2>🏘️ Per-Guild Visibility</h2>
              <p>Override module visibility for individual guilds.</p>
            </div>
            {guildError && <div className="error-banner">{guildError}</div>}
            {loadingGuilds ? (
              <div className="loading-container">
                <Spinner />
                <p>Loading guilds…</p>
              </div>
            ) : guilds.length === 0 ? (
              <p className="empty-state">
                The bot is not currently in any guilds.
              </p>
            ) : (
              <>
                <div className="guild-selector">
                  <label htmlFor="guild-select">Select guild:</label>
                  <select
                    id="guild-select"
                    value={selectedGuildId}
                    onChange={(event) => setSelectedGuildId(event.target.value)}
                  >
                    <option value="" disabled>
                      Select a guild
                    </option>
                    {guilds.map((guild) => (
                      <option key={guild.id} value={guild.id}>
                        {guild.name} ({guild.memberCount.toLocaleString()}{" "}
                        members)
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-danger leave-guild-btn"
                    onClick={handleLeaveGuild}
                    disabled={!selectedGuildId || leavingGuildId !== null}
                  >
                    {leavingGuildId === selectedGuildId ? (
                      <Spinner size="small" />
                    ) : (
                      "Remove Bot from Guild"
                    )}
                  </button>
                </div>
                {selectedGuild ? (
                  loadingGuildVisibility ? (
                    <div className="loading-container">
                      <Spinner />
                      <p>Loading module overrides…</p>
                    </div>
                  ) : (
                    <div className="module-list">
                      {modules.map((module) => {
                        const hasOverride = hasOwn(guildVisibility, module.id);
                        const inheritedValue = hasOwn(
                          globalVisibility,
                          module.id,
                        )
                          ? globalVisibility[module.id]
                          : true;
                        const effectiveValue = hasOverride
                          ? guildVisibility[module.id]
                          : inheritedValue;
                        const isSaving = savingGuildModuleId === module.id;
                        const statusLabel = effectiveValue
                          ? "Visible in this guild"
                          : "Hidden in this guild";
                        const hintText = hasOverride
                          ? effectiveValue
                            ? "Override active - forced to visible."
                            : "Override active - forced to hidden."
                          : `No override - inherits global (${inheritedValue ? "Visible" : "Hidden"}).`;
                        return (
                          <div key={module.id} className="module-row">
                            <div className="module-info">
                              <div className="module-heading-row">
                                <h3>{module.name}</h3>
                                <span
                                  className={`status-pill ${effectiveValue ? "status-pill--active" : "status-pill--hidden"}`}
                                >
                                  {statusLabel}
                                </span>
                              </div>
                              <p>{module.description}</p>
                              <div className="module-meta-row">
                                <span className="module-version">
                                  v{module.version}
                                </span>
                                <span className="module-visibility-hint">
                                  {hintText}
                                </span>
                              </div>
                            </div>
                            <button
                              className={`btn ${effectiveValue ? "btn-danger" : "btn-primary"}`}
                              onClick={() => handleToggleGuildModule(module.id)}
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <Spinner size="small" />
                              ) : effectiveValue ? (
                                "Hide in this Guild"
                              ) : (
                                "Show in this Guild"
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )
                ) : (
                  <p className="empty-state">
                    Select a guild to manage visibility overrides.
                  </p>
                )}
              </>
            )}
          </section>

          <section className="admin-section">
            <div className="section-header">
              <h2>🌐 Global Module Settings</h2>
              <p>Controls that affect every guild using a module.</p>
            </div>
            <CollapsibleSection
              title="Twitch live notifications"
              subtitle="Manage cross-guild Twitch polling"
              defaultCollapsed={false}
            >
              <p className="global-setting-description">
                Adjust how frequently the bot queries Twitch for new live
                streams. Lower intervals increase accuracy but also increase the
                load on your bot and on Twitch.
              </p>
              {liveModuleSettingsError && (
                <div className="error-banner">{liveModuleSettingsError}</div>
              )}
              {loadingLiveModuleSettings ? (
                <div className="loading-container">
                  <Spinner />
                  <p>Loading live settings…</p>
                </div>
              ) : !liveModuleSettings ? (
                <p className="empty-state">
                  No global settings are currently available for this module.
                </p>
              ) : (
                <div className="module-row global-setting-row">
                  <div className="global-setting-info">
                    <h3>Polling interval</h3>
                    <p>
                      Determines how often the bot checks Twitch for any guild
                      using live notifications.
                    </p>
                    <p className="global-setting-hint">
                      Minimum {liveModuleSettings.minValue} seconds · Default
                      {" "}
                      {liveModuleSettings.defaultValue} seconds
                    </p>
                  </div>
                  <div className="global-setting-controls">
                    <input
                      id="live-poll-interval"
                      type="number"
                      min={liveModuleSettings.minValue}
                      aria-label="Polling interval in seconds"
                      value={pollIntervalInput}
                      onChange={(event) =>
                        setPollIntervalInput(event.target.value)
                      }
                      placeholder={String(liveModuleSettings.defaultValue)}
                      className="global-setting-input-field"
                    />
                    <div className="global-setting-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSavePollInterval}
                        disabled={savingPollInterval}
                      >
                        {savingPollInterval ? (
                          <Spinner size="small" />
                        ) : (
                          "Save"
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleResetPollInterval}
                        disabled={savingPollInterval}
                      >
                        Reset to Default
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </CollapsibleSection>
          </section>

          <section className="admin-section">
            <div className="section-header">
              <h2>🚦 Feature Toggles</h2>
              <p>
                Enable or disable experimental functionality without editing
                environment variables.
              </p>
            </div>
            {featureError && <div className="error-banner">{featureError}</div>}
            {loadingFeatures ? (
              <div className="loading-container">
                <Spinner />
                <p>Loading features…</p>
              </div>
            ) : Object.keys(features).length === 0 ? (
              <p className="empty-state">No feature toggles available yet.</p>
            ) : (
              <div className="feature-list">
                {Object.entries(features).map(([key, value]) => {
                  const isSaving = savingFeatureKey === key;
                  return (
                    <div key={key} className="feature-row">
                      <div className="feature-info">
                        <h3>
                          {key === "allow_bot_invite"
                            ? "Allow Bot Invites"
                            : key}
                        </h3>
                        <p>
                          {key === "allow_bot_invite"
                            ? 'Controls access to the "Add Bot" flow within the dashboard.'
                            : "Toggle this feature."}
                        </p>
                      </div>
                      <button
                        className={`btn ${value ? "btn-danger" : "btn-primary"}`}
                        onClick={() => handleToggleFeature(key)}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <Spinner size="small" />
                        ) : value ? (
                          "Disable"
                        ) : (
                          "Enable"
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="admin-section admin-live-section">
            <CollapsibleSection
              title="📡 Twitch Live Notifications"
              count={adminLiveNotifications.length}
            >
              <p className="admin-live-description">
                Review and manage Twitch announcements across every guild.
              </p>
              {liveNotificationError && (
                <div className="error-banner">{liveNotificationError}</div>
              )}
              {loadingLiveNotifications ? (
                <div className="loading-container">
                  <Spinner />
                  <p>Loading live notifications…</p>
                </div>
              ) : adminLiveNotifications.length === 0 ? (
                <p className="empty-state">
                  No live notifications are currently configured.
                </p>
              ) : (
                <div className="admin-live-table-wrapper">
                  <table className="admin-live-table">
                    <thead>
                      <tr>
                        <th>Twitch username</th>
                        <th>Stream status</th>
                        <th>Discord guild</th>
                        <th>Auto delete</th>
                        <th>Tags</th>
                        <th>Edit when ends</th>
                        <th>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminLiveNotifications.map((notification) => (
                        <tr key={notification.id}>
                          <td>
                            <div className="admin-live-table__user">
                              <strong>{notification.twitch_username}</strong>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`status-pill ${
                                notification.is_live
                                  ? "status-pill--active"
                                  : "status-pill--hidden"
                              }`}
                            >
                              {notification.is_live ? "Live" : "Offline"}
                            </span>
                          </td>
                          <td>
                            <div className="admin-live-table__guild">
                              <span>{notification.guild_name}</span>
                              <span className="admin-live-table__channel">
                                {notification.channel_name
                                  ? `#${notification.channel_name}`
                                  : notification.channel_id}
                              </span>
                            </div>
                          </td>
                          <td className="boolean-cell">
                            <span
                              className={`boolean-pill ${
                                notification.auto_delete_previous === 1
                                  ? "boolean-pill--yes"
                                  : "boolean-pill--no"
                              }`}
                            >
                              {notification.auto_delete_previous === 1
                                ? "Yes"
                                : "No"}
                            </span>
                          </td>
                          <td className="boolean-cell">
                            <span
                              className={`boolean-pill ${
                                notification.show_tags === 1
                                  ? "boolean-pill--yes"
                                  : "boolean-pill--no"
                              }`}
                            >
                              {notification.show_tags === 1 ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="boolean-cell">
                            <span
                              className={`boolean-pill ${
                                notification.auto_edit_end === 1
                                  ? "boolean-pill--yes"
                                  : "boolean-pill--no"
                              }`}
                            >
                              {notification.auto_edit_end === 1 ? "Yes" : "No"}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger btn-compact"
                              onClick={() => handleAdminDeleteLive(notification)}
                              disabled={
                                deletingLiveNotificationId === notification.id
                              }
                            >
                              {deletingLiveNotificationId === notification.id
                                ? "Removing..."
                                : "Delete"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CollapsibleSection>
          </section>

          <section className="admin-section admin-live-section">
            <CollapsibleSection
              title="📺 YouTube Live Notifications"
              count={adminYouTubeLiveNotifications.length}
            >
              <p className="admin-live-description">
                Review and manage YouTube live alerts across every guild.
              </p>
              {youTubeLiveNotificationError && (
                <div className="error-banner">{youTubeLiveNotificationError}</div>
              )}
              {loadingYouTubeLiveNotifications ? (
                <div className="loading-container">
                  <Spinner />
                  <p>Loading YouTube live notifications…</p>
                </div>
              ) : adminYouTubeLiveNotifications.length === 0 ? (
                <p className="empty-state">
                  No YouTube live notifications are currently configured.
                </p>
              ) : (
                <div className="admin-live-table-wrapper">
                  <table className="admin-live-table">
                    <thead>
                      <tr>
                        <th>YouTube channel</th>
                        <th>Stream status</th>
                        <th>Discord guild</th>
                        <th>Auto delete</th>
                        <th>Tags</th>
                        <th>Edit when ends</th>
                        <th>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminYouTubeLiveNotifications.map((notification) => (
                        <tr key={notification.id}>
                          <td>
                            <div className="admin-live-table__user">
                              <strong>{notification.youtube_channel_id}</strong>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`status-pill ${
                                notification.is_live
                                  ? "status-pill--active"
                                  : "status-pill--hidden"
                              }`}
                            >
                              {notification.is_live ? "Live" : "Offline"}
                            </span>
                          </td>
                          <td>
                            <div className="admin-live-table__guild">
                              <span>{notification.guild_name}</span>
                              <span className="admin-live-table__channel">
                                {notification.channel_name
                                  ? `#${notification.channel_name}`
                                  : notification.channel_id}
                              </span>
                            </div>
                          </td>
                          <td className="boolean-cell">
                            <span
                              className={`boolean-pill ${
                                notification.auto_delete_previous === 1
                                  ? "boolean-pill--yes"
                                  : "boolean-pill--no"
                              }`}
                            >
                              {notification.auto_delete_previous === 1
                                ? "Yes"
                                : "No"}
                            </span>
                          </td>
                          <td className="boolean-cell">
                            <span
                              className={`boolean-pill ${
                                notification.show_tags === 1
                                  ? "boolean-pill--yes"
                                  : "boolean-pill--no"
                              }`}
                            >
                              {notification.show_tags === 1 ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="boolean-cell">
                            <span
                              className={`boolean-pill ${
                                notification.auto_edit_end === 1
                                  ? "boolean-pill--yes"
                                  : "boolean-pill--no"
                              }`}
                            >
                              {notification.auto_edit_end === 1 ? "Yes" : "No"}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger btn-compact"
                              onClick={() => handleAdminDeleteYouTube(notification)}
                              disabled={
                                deletingYouTubeNotificationId === notification.id
                              }
                            >
                              {deletingYouTubeNotificationId === notification.id
                                ? "Removing..."
                                : "Delete"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CollapsibleSection>
          </section>

          <section className="admin-section admin-live-section">
            <CollapsibleSection
              title="🎁 Drop Notifications"
              count={adminDropNotifications.length}
            >
              <p className="admin-live-description">
                Review the Twitch Drop alerts configured across every guild.
              </p>
              {dropNotificationError && (
                <div className="error-banner">{dropNotificationError}</div>
              )}
              {loadingDropNotifications ? (
                <div className="loading-container">
                  <Spinner />
                  <p>Loading drop notifications…</p>
                </div>
              ) : adminDropNotifications.length === 0 ? (
                <p className="empty-state">
                  No drop notifications are currently configured.
                </p>
              ) : (
                <div className="admin-live-table-wrapper">
                  <table className="admin-live-table drop-admin-table">
                    <thead>
                      <tr>
                        <th>Game name</th>
                        <th>Guild / Channel</th>
                        <th>Date start</th>
                        <th>Date end</th>
                        <th>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminDropNotifications.map((notification) => (
                        <tr key={notification.id}>
                          <td>
                            <div className="admin-live-table__user">
                              <strong>{notification.game_name}</strong>
                            </div>
                          </td>
                          <td>
                            <div className="admin-live-table__guild">
                              <span>{notification.guild_name}</span>
                              <span className="admin-live-table__channel">
                                {notification.channel_name
                                  ? `#${notification.channel_name}`
                                  : notification.channel_id}
                              </span>
                            </div>
                          </td>
                          <td>{formatDateTime(notification.last_drop_start_at)}</td>
                          <td>{formatDateTime(notification.last_drop_end_at)}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger btn-compact"
                              onClick={() => handleAdminDeleteDrop(notification)}
                              disabled={
                                deletingDropNotificationId === notification.id
                              }
                            >
                              {deletingDropNotificationId === notification.id
                                ? "Removing..."
                                : "Delete"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CollapsibleSection>
          </section>
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
